/**
 * THE SIGNED COPY OF A VARIATION, COMING BACK IN.
 *
 * 0137 gave the contractor a variation order to hold. This is the other half:
 * the sheet he signs and returns, scanned, filed against the variation for
 * good. Until it exists the company's file is weaker than the contractor's — he
 * has paper with the Project Head's seal on it and OVAL has a database row.
 *
 * WHY IT IS ITS OWN CARD AND NOT A SECOND WoPapers
 * ------------------------------------------------
 * WoPapers carries thirteen kinds of document behind a dropdown, because a work
 * order genuinely collects thirteen kinds. A variation collects exactly ONE.
 * Putting a chooser in front of a choice that has one option is how a person is
 * made to answer a question the screen already knows the answer to — and the
 * one way to get it wrong is to pick the wrong kind from the list.
 *
 * So: no dropdown, no kind, four ways in and all of them end in SIGNED_VO.
 *
 *   drag the file onto the card
 *   click the card and browse for it
 *   paste it with Ctrl+V, straight out of the scanner software
 *   photograph it, which on a phone opens the camera directly
 *
 * The last two are why this is not just WoPapers with a filter. A Project
 * Manager at Takarjala has the signed sheet in his hand and a phone in the
 * other; the Procurement Manager at Head Office has it in the scanner's
 * clipboard. Neither should have to save a file first.
 *
 * ONLY ONCE IT IS APPROVED
 * ------------------------
 * Drawn only for an APPROVED variation. There is nothing for a contractor to
 * sign until the Project Head has signed it first, and a card inviting somebody
 * to file the signed copy of a draft is a card that teaches people the document
 * means less than it does.
 *
 * NOTHING IS EVER DELETED
 * -----------------------
 * A second upload does not replace the first; it becomes the current one and
 * the first moves to "earlier scans". rpc_supersede_document refuses anything
 * that is not a vendor document, deliberately — these attachments are part of
 * the audit trail. If the wrong page was scanned, scan the right one; the
 * record then shows both, which is the truth of what happened.
 *
 * WHAT ACTUALLY STOPS THE WRONG PERSON
 * ------------------------------------
 * Not this file. fn_may_see_vo_docs and fn_may_edit_vo_docs in migration 0138,
 * through attach_read and attach_write, and the bucket is private so a path on
 * its own opens nothing. Everything here is the courtesy on top of that.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Camera, ChevronDown, ChevronRight, Download, ExternalLink,
  FileCheck2, FileText, Upload,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { dateTime } from '@/lib/format'
import { Card, Loading, useToast } from '@/components/ui'
import { saveAttachment } from '@/components/docs/shared'
import {
  listWoPapers, uploadWoPaper, woPaperViewUrl, WO_PAPER_BUCKET, type WoPaper,
} from '../lib/papers'

/** Mirrors fn_may_see_vo_docs(). The database is the authority. */
const MAY_SEE = [
  'admin', 'procurement_manager', 'procurement_head', 'project_head',
  'project_manager', 'cmd', 'cxo', 'accounts_spoc',
] as const

/**
 * Mirrors fn_may_edit_vo_docs(). The four who own the contract file, plus the
 * Project Manager — he is at site when the contractor signs it, and he is
 * stage 1 of the chain that approved it. Accounts is deliberately absent.
 */
const MAY_EDIT = [
  'admin', 'procurement_manager', 'procurement_head', 'project_head',
  'project_manager',
] as const

const KIND = 'SIGNED_VO'

/**
 * 25 MB, against a bucket that would take 50.
 *
 * Refused here rather than at the bucket on purpose: a 40 MB upload over a site
 * connection takes minutes and then fails with a storage error nobody can act
 * on. A phone photograph is four or five megabytes and a scanned A4 page is
 * under one, so anything past 25 is a camera left on its highest setting — and
 * the message below says so and says what to do.
 */
const MAX_BYTES = 25 * 1024 * 1024

const ACCEPT = 'image/*,application/pdf'

function readableSize(bytes: number | null | undefined): string {
  if (!bytes && bytes !== 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function VoSignedCopy({ voId, status }: { voId: string; status: string }) {
  const { profile } = useAuth()
  const toast = useToast()
  const [papers, setPapers] = useState<WoPaper[]>([])
  const [names, setNames] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const [showOld, setShowOld] = useState(false)
  const [preview, setPreview] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const cameraInput = useRef<HTMLInputElement>(null)

  const role = profile?.role ?? ''
  const maySee = (MAY_SEE as readonly string[]).includes(role)
  const mayEdit = (MAY_EDIT as readonly string[]).includes(role)
  const approved = status === 'APPROVED'

  const load = useCallback(async () => {
    setLoading(true)
    const rows = await listWoPapers(voId, 'VO')
    const signed = rows.filter((p) => p.kind === KIND)
    setPapers(signed)

    // Who filed it. One query for every uploader on the card, not one per row.
    const ids = [...new Set(signed.map((p) => p.uploaded_by).filter(Boolean))] as string[]
    if (ids.length) {
      const { data } = await supabase.from('profiles').select('id, full_name').in('id', ids)
      const m: Record<string, string> = {}
      for (const p of (data ?? []) as { id: string; full_name: string }[]) {
        m[p.id] = p.full_name
      }
      setNames(m)
    }
    setLoading(false)
  }, [voId])

  useEffect(() => { void load() }, [load])

  // A thumbnail of the current scan, so somebody can see at a glance that the
  // right page went in rather than taking the file name's word for it. Minted
  // on load and only for an image; a PDF gets its icon, because rendering one
  // in a card would mean shipping a PDF engine to draw a postage stamp.
  const current = papers.length ? papers[papers.length - 1] : null
  const currentId = current?.id
  const currentPath = current?.file_path
  const currentIsImage = !!current?.mime_type?.startsWith('image/')

  useEffect(() => {
    let alive = true
    setPreview(null)
    if (!currentPath || !currentIsImage) return
    void woPaperViewUrl(currentPath)
      .then((u) => { if (alive) setPreview(u) })
      .catch(() => { /* the row still lists; a missing thumbnail is not an error */ })
    return () => { alive = false }
  }, [currentId, currentPath, currentIsImage])

  const put = useCallback(async (file: File) => {
    if (file.size > MAX_BYTES) {
      toast.error(
        `${file.name} is ${readableSize(file.size)}. The limit is 25 MB — if that is a `
        + `photograph, drop the camera to a lower setting and take it again.`)
      return
    }
    const ok = file.type.startsWith('image/') || file.type === 'application/pdf'
    if (!ok) {
      toast.error('A scan has to be a PDF or a photograph. That file is neither.')
      return
    }
    setBusy('upload')
    try {
      await uploadWoPaper(voId, KIND, file, 'VO')
      toast.success('The signed copy is on file.')
      await load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'That file could not be attached')
    } finally { setBusy(null) }
  }, [voId, load, toast])

  // PASTE, straight out of the scanner software.
  //
  // A window listener rather than one on a focused element, because nobody
  // clicks a card before pressing Ctrl+V. It is safe to listen this widely: the
  // handler does nothing at all unless the clipboard actually carries a FILE,
  // so pasting text into any box on this page never reaches it.
  useEffect(() => {
    if (!approved || !mayEdit) return
    const onPaste = (e: ClipboardEvent) => {
      const f = e.clipboardData?.files?.[0]
      if (!f) return
      e.preventDefault()
      void put(f)
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [approved, mayEdit, put])

  if (!maySee || !approved) return null

  async function open(p: WoPaper) {
    setBusy(p.id)
    try {
      window.open(await woPaperViewUrl(p.file_path), '_blank', 'noopener')
    } catch { toast.error('That file could not be opened.') }
    finally { setBusy(null) }
  }

  async function save(p: WoPaper) {
    setBusy(p.id)
    try {
      await saveAttachment(WO_PAPER_BUCKET, p.file_path, p.file_name)
    } catch { toast.error('That file could not be downloaded.') }
    finally { setBusy(null) }
  }

  const filedBy = (p: WoPaper) => {
    const who = p.uploaded_by ? names[p.uploaded_by] : null
    return who ? `Filed by ${who} · ${dateTime(p.uploaded_at)}` : `Filed ${dateTime(p.uploaded_at)}`
  }

  const earlier = papers.slice(0, -1).reverse()

  const Actions = ({ p }: { p: WoPaper }) => (
    <>
      <button className="btn-ghost btn-sm shrink-0" disabled={busy === p.id}
              title="Open in a new tab" onClick={() => void open(p)}>
        <ExternalLink className="h-4 w-4" /> Open
      </button>
      <button className="btn-ghost btn-sm shrink-0" disabled={busy === p.id}
              title="Save to this computer" onClick={() => void save(p)}>
        <Download className="h-4 w-4" /> Download
      </button>
    </>
  )

  return (
    <Card
      title="The copy the contractor signed"
      subtitle="Scan the sheet he signed and returned. That scan, not the copy OVAL issued, is what the company holds on file."
    >
      {loading ? <Loading label="Reading the file…" /> : (
        <div className="space-y-3">

          {/* ---------- what is on file ---------- */}
          {current && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5">
              {preview
                ? <img src={preview} alt=""
                       className="h-14 w-11 shrink-0 rounded border border-emerald-300 bg-white object-cover" />
                : <span className="flex h-14 w-11 shrink-0 items-center justify-center rounded border border-emerald-300 bg-white">
                    <FileText className="h-5 w-5 text-emerald-600" />
                  </span>}
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-900">
                  <FileCheck2 className="h-4 w-4 shrink-0" />
                  <span className="truncate">{current.file_name}</span>
                </p>
                <p className="mt-0.5 text-xs text-emerald-800">
                  {filedBy(current)}
                  {current.size_bytes ? ` · ${readableSize(current.size_bytes)}` : ''}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Actions p={current} />
              </div>
            </div>
          )}

          {/* ---------- putting one in ---------- */}
          {mayEdit ? (
            <div
              role="button"
              tabIndex={0}
              onClick={() => fileInput.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault(); fileInput.current?.click()
                }
              }}
              onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault()
                setDragging(false)
                const f = e.dataTransfer.files?.[0]
                if (f) void put(f)
              }}
              className={`cursor-pointer rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors ${
                dragging
                  ? 'border-amber-500 bg-amber-50'
                  : 'border-slate-300 bg-slate-50 hover:border-amber-400 hover:bg-amber-50/40'
              }`}
            >
              {busy === 'upload' ? (
                <p className="text-sm font-medium text-slate-700">Uploading the scan…</p>
              ) : (
                <>
                  <Upload className="mx-auto h-6 w-6 text-slate-400" />
                  <p className="mt-1.5 text-sm font-semibold text-slate-700">
                    {current
                      ? 'Drop a corrected or additional scan here'
                      : 'Drop the signed scan here, or click to choose a file'}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    You can also paste it with Ctrl+V. PDF or photograph, up to 25 MB.
                  </p>
                  <button
                    type="button"
                    className="btn-secondary btn-sm mx-auto mt-2.5"
                    onClick={(e) => { e.stopPropagation(); cameraInput.current?.click() }}
                  >
                    <Camera className="h-4 w-4" /> Take a photo
                  </button>
                </>
              )}

              <input ref={fileInput} type="file" className="hidden" accept={ACCEPT}
                     onChange={(e) => {
                       const f = e.target.files?.[0]
                       if (f) void put(f)
                       e.target.value = ''
                     }} />
              {/* capture opens the camera directly on a phone and is ignored on
                  a desktop, where the button falls back to an ordinary chooser. */}
              <input ref={cameraInput} type="file" className="hidden" accept="image/*"
                     capture="environment"
                     onChange={(e) => {
                       const f = e.target.files?.[0]
                       if (f) void put(f)
                       e.target.value = ''
                     }} />
            </div>
          ) : !current && (
            <p className="rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs text-slate-500">
              Not signed back yet. Procurement or the Project Manager of the site files
              the scan once the contractor returns it.
            </p>
          )}

          {/* ---------- everything filed before the current one ---------- */}
          {earlier.length > 0 && (
            <div className="border-t border-slate-200 pt-2">
              <button className="flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800"
                      onClick={() => setShowOld((v) => !v)}>
                {showOld ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                {showOld ? 'Hide' : 'Show'} {earlier.length} earlier scan{earlier.length === 1 ? '' : 's'}
              </button>
              {showOld && (
                <div className="mt-2 space-y-1">
                  {earlier.map((p) => (
                    <div key={p.id}
                         className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm">
                      <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                      <span className="min-w-0 flex-1 truncate">{p.file_name}</span>
                      <span className="hidden shrink-0 text-xs text-slate-400 md:inline">
                        {filedBy(p)}
                      </span>
                      <Actions p={p} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  )
}
